import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useCompensationComponentSave } from "@/queries/use-compensation-components.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiCompensationComponent,
  ApiCompensationComponentKind,
  ApiCompensationComponentPayload,
} from "@/types/compensation-component.types.ts";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  Switch,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface CompensationComponentEditModalProps extends ModalProps {
  component?: ApiCompensationComponent;
  nextSort: number;
}

const flags = [
  "is_included_in_gross",
  "is_payable_to_employee",
  "is_included_in_total_cost",
  "is_off_payroll",
  "is_statutory_base",
] as const;

export function CompensationComponentEditModal({
  component,
  nextSort,
  ...props
}: CompensationComponentEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const schema = z.object({
    title: z.string().trim().min(1, t("errors:field_required")),
    kind: z.enum(ApiCompensationComponentKind),
    is_included_in_gross: z.boolean(),
    is_payable_to_employee: z.boolean(),
    is_included_in_total_cost: z.boolean(),
    is_off_payroll: z.boolean(),
    is_statutory_base: z.boolean(),
  });
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: component ?? {
      title: "",
      kind: ApiCompensationComponentKind.earning,
      is_included_in_gross: true,
      is_payable_to_employee: true,
      is_included_in_total_cost: true,
      is_off_payroll: false,
      is_statutory_base: false,
    },
  });
  const save = useCompensationComponentSave(control);
  const kindOptions = Object.values(ApiCompensationComponentKind).map((kind) => ({
    id: kind,
    title: t(`core:compensation_component_kind_${kind}`),
  }));

  const onSubmit = handleSubmit(async (values) => {
    const payload: ApiCompensationComponentPayload = {
      ...values,
      sort: component?.sort ?? nextSort,
      default_per_type: component?.default_per_type ?? null,
      default_currency_code: component?.default_currency_code ?? null,
    };
    try {
      const savedComponent = await save.mutateAsync({ id: component?.id, payload });
      analyticsService.trackEvent(
        component
          ? analyticEvents.compensationTypes.edited
          : analyticEvents.compensationTypes.created,
        { [analyticProperties.id]: savedComponent.id }
      );
      close(props.id);
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t(
        component ? "core:edit_compensation_component" : "core:create_compensation_component"
      )}
      withCloser
      actions={
        <Stack gap="sm">
          <ServerErrorField errors={errors} />
          <Button type="submit" size="lg" isLoading={save.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            label={t("common:title")}
            required
            autoFocus
            {...register("title")}
            error={errors.title?.message}
          />
          <Controller
            control={control}
            name="kind"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("core:compensation_component_kind")}
                required
                options={kindOptions}
                emptyTitle={t("common:select")}
                value={value}
                onChange={(newValue) => {
                  onChange(newValue);
                  if (newValue === ApiCompensationComponentKind.employer_contribution) {
                    setValue("is_payable_to_employee", false);
                  }
                }}
              />
            )}
          />
          <Title
            header={t("core:compensation_component_calculation_settings")}
            size="xs"
            paddingTop
            paddingBottom
          />
          <Stack gap={"sm"}>
            {flags.map((flag) => (
              <Controller
                key={flag}
                control={control}
                name={flag}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={onChange}
                    size="sm"
                    title={t(`core:compensation_component_${flag}`)}
                    description={t(`core:compensation_component_${flag}_description`)}
                  />
                )}
              />
            ))}
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
